// apps/share-web/src/chart/section-strip.ts
//
// Лента структуры песни под шапкой слоя чтения (words page v2, спека
// 2026-09-14): чипы всех НАЗВАННЫХ секций по порядку. Текущая и следующая —
// полным именем, остальные — сокращением («Verse 1» → V1, «Pre-Chorus» → PC).
// Сергей: «структура песни сверху полосой нужна постоянно… текущая и следующая
// часть полным названием, а не первой буквой; она должна скроллиться
// влево-вправо, а вначале подскролл идёт на текущую часть».
//
// Ряд заменяет собой «сейчас → дальше» (chart-now-next.ts) в СЛОЕ; в панели
// плеера тот ряд остаётся. Ленту монтирует только поверхность, у которой есть
// позиция (лайв или ведение по темпу): без позиции чипы стояли бы без «сейчас»,
// а это структура ради структуры — она и так на листе.
//
// Тап по чипу — ИНДЕКС секции наружу: чем на него ответить (прокрутить лист,
// перезавести часы), знает владелец, не лента.

import { sectionIndexAt, type SectionSec } from './chart-follow.js';

/**
 * Сокращение имени секции: первые буквы слов (через пробел или дефис) плюс
 * хвостовые цифры. «Verse 1» → V1, «Pre-Chorus» → PC, «Chorus» → C,
 * «Intro» → I, «Куплет 2» → К2. Пустое — пустое.
 */
export function abbrevSection(name: string): string {
  const n = (name ?? '').trim();
  if (n === '') return '';
  const m = /^(.*?)(\d+)?$/.exec(n);
  const words = (m?.[1] ?? n).trim().split(/[\s-]+/).filter(Boolean);
  const letters = words.map((w) => w[0].toUpperCase()).join('');
  return letters + (m?.[2] ?? '');
}

export interface SectionStripDeps {
  /** ПОЛНОЕ расписание — с безымянными тоже; чипы получают только названные. */
  sections: SectionSec[];
  /** Тап по чипу — индекс секции в `sections`. */
  onJump: (sectionIdx: number) => void;
  /** Подскролл ряда к чипу. В проде — по offsetLeft; в тестах подставляется. */
  scrollTo?: (row: HTMLElement, chip: HTMLElement) => void;
}

export interface SectionStripHandle {
  el: HTMLElement;
  /** Один кадр. `null` — позиции нет: подсветка снимается. */
  sync(posSec: number | null): void;
  destroy(): void;
}

const nameOf = (s: SectionSec | undefined): string => (s?.name ?? '').trim();

/** Следующая НАЗВАННАЯ секция после `i` — та, которую читатель найдёт на листе. */
function nextNamedIdx(sections: readonly SectionSec[], i: number): number {
  for (let j = i + 1; j < sections.length; j++) if (nameOf(sections[j]) !== '') return j;
  return -1;
}

const defaultScrollTo = (row: HTMLElement, chip: HTMLElement): void => {
  row.scrollLeft = Math.max(0, chip.offsetLeft - 10);
};

/** `null` — называть в этой песне нечего, и ленты быть не должно. */
export function mountSectionStrip(host: HTMLElement, deps: SectionStripDeps): SectionStripHandle | null {
  if (!deps.sections.some((s) => nameOf(s) !== '')) return null;
  const scrollTo = deps.scrollTo ?? defaultScrollTo;

  const row = document.createElement('div');
  row.className = 'cw-strip';
  row.setAttribute('role', 'list');

  // Чип → индекс секции. Безымянные секции чипа не имеют.
  const chips = new Map<number, HTMLButtonElement>();
  deps.sections.forEach((s, i) => {
    const name = nameOf(s);
    if (name === '') return;
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'cw-chip';
    b.textContent = abbrevSection(name);
    b.title = name;
    b.dataset.section = String(i);
    b.addEventListener('click', () => deps.onJump(i));
    row.appendChild(b);
    chips.set(i, b);
  });
  host.appendChild(row);

  let lastNow = -2;   // −2 = ещё не синхронизировались; −1 = позиции нет
  const paint = (now: number, raw: number): void => {
    const next = raw < 0 ? -1 : nextNamedIdx(deps.sections, raw);
    for (const [i, b] of chips) {
      const isNow = i === now;
      const isNext = i === next;
      b.classList.toggle('is-now', isNow);
      b.classList.toggle('is-next', isNext);
      b.textContent = isNow || isNext ? nameOf(deps.sections[i]) : abbrevSection(nameOf(deps.sections[i]));
      if (isNow) b.setAttribute('aria-current', 'true');
      else b.removeAttribute('aria-current');
    }
  };

  return {
    el: row,
    sync(posSec) {
      // Безымянная секция (проигрыш) — «сейчас» никого: то же правило, что у
      // подсветки листа (chart-follow.ts): чужая подсветка хуже её отсутствия.
      // «Дальше» при этом показывается — следующая названная после позиции.
      const raw = posSec == null ? -1 : sectionIndexAt(deps.sections, posSec);
      const i = raw >= 0 && nameOf(deps.sections[raw]) !== '' ? raw : -1;
      const key = raw < 0 ? -1 : (i >= 0 ? i : -1000 - raw);
      if (key === lastNow) return;
      lastNow = key;
      paint(i, raw);
      const chip = i >= 0 ? chips.get(i) : undefined;
      if (chip) scrollTo(row, chip);
    },
    destroy() { row.remove(); },
  };
}
